import { useEffect, useState } from "react";
import { apiFetch } from "./api";
import { canonicalRole, getUserStatus } from "./utils";

export default function UserModal({
  user,
  onClose,
  onUpdated,
  onShowUser,
  canEditRoster = false,
  canManageUsers = false,
  roster = [],
  teams = [],
  squads = [],
  positions = [],
  highSchools = [],
}) {
  const [form, setForm] = useState({
    team: user?.team || "",
    squad: user?.squad || "",
    position: user?.position || "",
    height: user?.height || "",
    weight: user?.weight || "",
    graduation_year: user?.graduation_year || "",
    birthdate: user?.birthdate ? String(user.birthdate).slice(0, 10) : "",
    high_school: user?.high_school || "",
    two_k_pr: user?.two_k_pr || "",
    role: user?.role_key || user?.role?.toLowerCase() || "",
    secondary_role: user?.secondary_role_key || user?.secondary_role?.toLowerCase() || "",
  });
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [parentRelationships, setParentRelationships] = useState([]);
  const [loadingRelationships, setLoadingRelationships] = useState(false);
  const [linkingParent, setLinkingParent] = useState(false);
  const [selectedParentId, setSelectedParentId] = useState("");
  const [emailDraft, setEmailDraft] = useState(user?.email || "");
  const [showPermissions, setShowPermissions] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [updatingPermission, setUpdatingPermission] = useState({});

  const selectedRoleKey = canonicalRole(user);
  const selectedRoleLabel = selectedRoleKey === "coach" ? "Coach" : selectedRoleKey === "athlete" ? "Athlete" : selectedRoleKey === "parent" ? "Parent" : "";
  const isCoachRecord = selectedRoleKey === "coach";
  const isAthleteRecord = selectedRoleKey === "athlete";
  const isParentRecord = selectedRoleKey === "parent";

  const notifyUpdated = typeof onUpdated === "function" ? onUpdated : () => {};

  // Initialize form when user changes
  useEffect(() => {
    if (user) {
      setForm({
        team: user.team || "",
        squad: user.squad || "",
        position: user.position || "",
        height: user.height || "",
        weight: user.weight || "",
        graduation_year: user.graduation_year || "",
        birthdate: user.birthdate ? String(user.birthdate).slice(0, 10) : "",
        high_school: user.high_school || "",
        two_k_pr: user.two_k_pr || "",
        role: user.role_key || user.role?.toLowerCase() || "",
        secondary_role: user.secondary_role_key || user.secondary_role?.toLowerCase() || "",
      });
      setEmailDraft(user.email || "");
      setShowPermissions(false);
      setPermissions([]);
      setUpdatingPermission({});
    }
  }, [user]);

  // Load parent-athlete relationships when modal opens
  useEffect(() => {
    if (canManageUsers && user && (isAthleteRecord || isParentRecord)) {
      setLoadingRelationships(true);
      apiFetch("/parent/relationships")
        .then((relationships) => {
          const userId = user.user_id || user.id;
          if (isAthleteRecord) {
            const athleteRels = relationships.filter((r) => r.athlete_user_id === userId);
            setParentRelationships(athleteRels);
          } else if (isParentRecord) {
            const parentRels = relationships.filter((r) => r.parent_user_id === userId);
            setParentRelationships(parentRels);
          }
        })
        .catch((e) => {
          console.error("Failed to load relationships:", e);
          setParentRelationships([]);
        })
        .finally(() => {
          setLoadingRelationships(false);
        });
    }
  }, [user, canManageUsers, isAthleteRecord, isParentRecord]);

  useEffect(() => {
    if (!showPermissions || !canManageUsers || !user) return;
    let active = true;
    const userId = user.user_id ?? user.id;
    setLoadingPermissions(true);
    apiFetch(`/users/${userId}/permissions`)
      .then((data) => {
        if (!active) return;
        setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
      })
      .catch((e) => {
        if (!active) return;
        setModalError(e?.message || "Failed to load permissions");
        setPermissions([]);
      })
      .finally(() => {
        if (!active) return;
        setLoadingPermissions(false);
      });
    return () => {
      active = false;
    };
  }, [showPermissions, canManageUsers, user]);

  const handleSendInvite = async () => {
    if (!user || !canManageUsers) return;

    const userId = user.user_id ?? user.id;
    if (!userId || !user.email) {
      alert("Cannot send invite: missing user ID or email");
      return;
    }

    setSendingInvite(true);
    setModalError("");
    try {
      const result = await apiFetch(`/users/${userId}/send-invite`, { method: "POST" });
      alert(result.message || "Invite sent successfully!");
      notifyUpdated();
    } catch (e) {
      setModalError(e?.message || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  const handleToggleStatus = async () => {
    if (!user || !canManageUsers) return;

    const userId = user.user_id ?? user.id;
    const currentStatus = user.is_active;
    const newStatus = !currentStatus;
    const action = newStatus ? "activate" : "deactivate";

    if (!window.confirm(`Are you sure you want to ${action} this account?`)) return;

    setTogglingStatus(true);
    setModalError("");
    try {
      const result = await apiFetch(`/users/${userId}/status`, {
        method: "PATCH",
        body: JSON.stringify({ is_active: newStatus })
      });
      alert(result.message || `Account ${action}d successfully`);
      onClose();
      notifyUpdated();
    } catch (e) {
      setModalError(e?.message || `Failed to ${action} account`);
    } finally {
      setTogglingStatus(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setModalError("");
    try {
      const userId = user.user_id ?? user.id;
      if (!userId) return;

      const tasks = [];

      if (canEditRoster) {
        if (isAthleteRecord && form.two_k_pr.trim() && !/^[0-9]{2}:[0-5][0-9]:[0-5][0-9]$/.test(form.two_k_pr.trim())) {
          setModalError("2K PR must be formatted as hh:mm:ss, e.g. 00:07:15");
          setSaving(false);
          return;
        }

        const payload = isCoachRecord
          ? {
              team: form.team.trim() ? form.team.trim() : null,
              squad: form.squad.trim() ? form.squad.trim() : null,
            }
          : isAthleteRecord
          ? {
              team: form.team.trim() ? form.team.trim() : null,
              squad: form.squad.trim() ? form.squad.trim() : null,
              position: form.position.trim() ? form.position.trim() : null,
              height: form.height.trim() ? form.height.trim() : null,
              weight: form.weight.trim() ? form.weight.trim() : null,
              graduation_year: form.graduation_year !== "" ? form.graduation_year : null,
              birthdate: form.birthdate.trim() ? form.birthdate.trim() : null,
              high_school: form.high_school.trim() ? form.high_school.trim() : null,
              two_k_pr: form.two_k_pr.trim() ? form.two_k_pr.trim() : null,
            }
          : {
              team: form.team.trim() ? form.team.trim() : null,
              squad: form.squad.trim() ? form.squad.trim() : null,
              position: form.position.trim() ? form.position.trim() : null,
              height: form.height.trim() ? form.height.trim() : null,
              weight: form.weight.trim() ? form.weight.trim() : null,
            };
        tasks.push(
          apiFetch(`/users/${userId}/roster`, {
            method: "PATCH",
            body: JSON.stringify(payload),
          })
        );
      }

      if (canManageUsers) {
        const currentRoleKey = user.role_key || user.role?.toLowerCase() || "";
        const currentSecondaryKey = user.secondary_role_key || user.secondary_role?.toLowerCase() || "";
        const nextRole = form.role;
        const nextSecondary = form.secondary_role;
        const roleChanged = nextRole !== currentRoleKey;
        const secondaryChanged = nextSecondary !== currentSecondaryKey;
        if (roleChanged || secondaryChanged) {
          const body = {};
          if (roleChanged) body.role = nextRole;
          if (secondaryChanged) body.secondary_role = nextSecondary || null;
          tasks.push(apiFetch(`/users/${userId}/role`, { method: "PATCH", body: JSON.stringify(body) }));
        }

        const nextEmail = emailDraft.trim();
        const currentEmail = (user.email || "").trim();
        if (!nextEmail) {
          setModalError("Email is required");
          return;
        }
        if (nextEmail !== currentEmail) {
          tasks.push(
            apiFetch(`/users/${userId}/email`, {
              method: "PATCH",
              body: JSON.stringify({ email: nextEmail }),
            }).then(() => {
              if (typeof onShowUser === "function") {
                onShowUser({ ...user, email: nextEmail });
              }
            })
          );
        }
      }

      if (tasks.length === 0) {
        setModalError("No changes to save");
        return;
      }

      await Promise.all(tasks);
      onClose();
      notifyUpdated();
    } catch (e) {
      setModalError(e?.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const handleTogglePermission = async (permissionKey, nextValue) => {
    if (!user || !canManageUsers) return;
    const userId = user.user_id ?? user.id;
    if (!userId) return;
    setUpdatingPermission((prev) => ({ ...prev, [permissionKey]: true }));
    setPermissions((prev) =>
      prev.map((perm) =>
        perm.key === permissionKey ? { ...perm, allowed: nextValue } : perm
      )
    );
    try {
      const data = await apiFetch(`/users/${userId}/permissions`, {
        method: "PATCH",
        body: JSON.stringify({ permission: permissionKey, allowed: nextValue }),
      });
      setPermissions(Array.isArray(data?.permissions) ? data.permissions : []);
      notifyUpdated();
    } catch (e) {
      setPermissions((prev) =>
        prev.map((perm) =>
          perm.key === permissionKey ? { ...perm, allowed: !nextValue } : perm
        )
      );
      setModalError(e?.message || "Failed to update permission");
    } finally {
      setUpdatingPermission((prev) => ({ ...prev, [permissionKey]: false }));
    }
  };

  const openLinkedUser = (userId) => {
    if (!userId || typeof onShowUser !== "function") return;
    const target = roster.find((u) => (u.user_id || u.id) === userId);
    if (target) {
      onShowUser(target);
    }
  };

  const linkButtonStyle = {
    background: "none",
    border: "none",
    padding: 0,
    color: "var(--accent)",
    cursor: "pointer",
    textDecoration: "underline",
    font: "inherit"
  };

  if (!user) return null;

  return (
    <div className="modalOverlay" onClick={onClose}>
      <div className="modalCard roster-modal" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="modal-close"
          aria-label="Close roster details"
          onClick={onClose}
        >
          &times;
        </button>
        <div className="roster-modal-header">
          <h3 className="h2 roster-modal-title">{user.name || "User"}</h3>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            {selectedRoleKey && (
              <span className={`roster-role-chip roster-role-${selectedRoleKey}`}>
                {user.role || selectedRoleLabel}
              </span>
            )}
            {user.secondary_role && (
              <span className={`roster-role-chip roster-role-${(user.secondary_role_key || user.secondary_role).toLowerCase()}`}>
                {user.secondary_role}
              </span>
            )}
            <span className={`roster-status-badge roster-status-${getUserStatus(user).type}`}>
              {getUserStatus(user).label}
            </span>
          </div>
        </div>
        <div className="stack roster-modal-body">
          {canManageUsers && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <label className="stack" style={{ gap: 4, flex: 1, minWidth: 120 }}>
                <span className="help">Role</span>
                <select
                  className="input"
                  value={form.role}
                  onChange={(e) => setForm((prev) => ({ ...prev, role: e.target.value }))}
                >
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                  <option value="parent">Parent</option>
                </select>
              </label>
              <label className="stack" style={{ gap: 4, flex: 1, minWidth: 120 }}>
                <span className="help">Secondary Role</span>
                <select
                  className="input"
                  value={form.secondary_role}
                  onChange={(e) => setForm((prev) => ({ ...prev, secondary_role: e.target.value }))}
                >
                  <option value="">None</option>
                  <option value="athlete">Athlete</option>
                  <option value="coach">Coach</option>
                  <option value="parent">Parent</option>
                </select>
              </label>
            </div>
          )}
          {isCoachRecord ? (
            <>
              <div><strong>Name:</strong> {user.name || "—"}</div>
              {canManageUsers ? (
                <label className="stack" style={{ gap: 4, marginTop: 8 }}>
                  <span className="help">Email</span>
                  <input
                    className="input"
                    type="email"
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    placeholder="name@example.com"
                  />
                </label>
              ) : (
                <div><strong>Email:</strong> {user.email || "—"}</div>
              )}
              {canEditRoster ? (
                <>
                  <label className="stack" style={{ gap: 4, marginTop: 8 }}>
                    <span className="help">Team</span>
                    <select
                      className="input"
                      value={form.team}
                      onChange={(e) => setForm((prev) => ({ ...prev, team: e.target.value }))}
                    >
                      <option value="">—</option>
                      {teams.map((team) => (
                        <option key={team} value={team}>{team}</option>
                      ))}
                    </select>
                  </label>
                  <label className="stack" style={{ gap: 4, marginTop: 8 }}>
                    <span className="help">Squad</span>
                    <select
                      className="input"
                      value={form.squad}
                      onChange={(e) => setForm((prev) => ({ ...prev, squad: e.target.value }))}
                    >
                      <option value="">—</option>
                      {squads.map((squad) => (
                        <option key={squad} value={squad}>{squad}</option>
                      ))}
                    </select>
                  </label>
                </>
              ) : (
                <>
                  <div><strong>Team:</strong> {user.team || "—"}</div>
                  <div><strong>Squad:</strong> {user.squad || "—"}</div>
                </>
              )}
            </>
          ) : isParentRecord ? (
            canManageUsers && (
              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Email</span>
                <input
                  className="input"
                  type="email"
                  value={emailDraft}
                  onChange={(e) => setEmailDraft(e.target.value)}
                  placeholder="name@example.com"
                />
              </label>
            )
          ) : canEditRoster ? (
            <>
              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Team</span>
                <select
                  className="input"
                  value={form.team}
                  onChange={(e) => setForm((prev) => ({ ...prev, team: e.target.value }))}
                >
                  <option value="">—</option>
                  {teams.map((team) => (
                    <option key={team} value={team}>
                      {team}
                    </option>
                  ))}
                </select>
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Squad</span>
                <select
                  className="input"
                  value={form.squad}
                  onChange={(e) => setForm((prev) => ({ ...prev, squad: e.target.value }))}
                >
                  <option value="">—</option>
                  {squads.map((squad) => (
                    <option key={squad} value={squad}>
                      {squad}
                    </option>
                  ))}
                </select>
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Position</span>
                <select
                  className="input"
                  value={form.position}
                  onChange={(e) => setForm((prev) => ({ ...prev, position: e.target.value }))}
                >
                  <option value="">—</option>
                  {positions.map((position) => (
                    <option key={position} value={position}>
                      {position}
                    </option>
                  ))}
                </select>
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Height</span>
                <input
                  className="input"
                  value={form.height}
                  onChange={(e) => setForm((prev) => ({ ...prev, height: e.target.value }))}
                  placeholder="e.g. 6 ft 2 in"
                />
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Weight</span>
                <input
                  className="input"
                  value={form.weight}
                  onChange={(e) => setForm((prev) => ({ ...prev, weight: e.target.value }))}
                  placeholder="e.g. 185 lbs"
                />
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Graduation Year</span>
                <input
                  className="input"
                  type="number"
                  value={form.graduation_year}
                  onChange={(e) => setForm((prev) => ({ ...prev, graduation_year: e.target.value }))}
                  placeholder="e.g. 2027"
                />
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">Birthdate</span>
                <input
                  className="input"
                  type="date"
                  value={form.birthdate}
                  onChange={(e) => setForm((prev) => ({ ...prev, birthdate: e.target.value }))}
                />
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">High School</span>
                <select
                  className="input"
                  value={form.high_school}
                  onChange={(e) => setForm((prev) => ({ ...prev, high_school: e.target.value }))}
                >
                  <option value="">—</option>
                  {highSchools.map((hs) => (
                    <option key={hs} value={hs}>
                      {hs}
                    </option>
                  ))}
                </select>
              </label>

              <label className="stack" style={{ gap: 4 }}>
                <span className="help">2K PR</span>
                <input
                  className="input"
                  value={form.two_k_pr}
                  onChange={(e) => setForm((prev) => ({ ...prev, two_k_pr: e.target.value }))}
                  placeholder="hh:mm:ss, e.g. 00:07:15"
                />
              </label>
              {canManageUsers && (
                <label className="stack" style={{ gap: 4 }}>
                  <span className="help">Email</span>
                  <input
                    className="input"
                    type="email"
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    placeholder="name@example.com"
                  />
                </label>
              )}
            </>
          ) : (
            <>
              <div className="stack" style={{ gap: 4 }}>
                <span className="help">Team</span>
                <div>{user.team || "—"}</div>
              </div>
              <div className="stack" style={{ gap: 4 }}>
                <span className="help">Squad</span>
                <div>{user.squad || "—"}</div>
              </div>
              <div className="stack" style={{ gap: 4 }}>
                <span className="help">Position</span>
                <div>{user.position || "—"}</div>
              </div>
              <div className="stack" style={{ gap: 4 }}>
                <span className="help">Height</span>
                <div>{user.height || "—"}</div>
              </div>
              <div className="stack" style={{ gap: 4 }}>
                <span className="help">Weight</span>
                <div>{user.weight || "—"}</div>
              </div>
              {canManageUsers ? (
                <label className="stack" style={{ gap: 4 }}>
                  <span className="help">Email</span>
                  <input
                    className="input"
                    type="email"
                    value={emailDraft}
                    onChange={(e) => setEmailDraft(e.target.value)}
                    placeholder="name@example.com"
                  />
                </label>
              ) : (
                <div className="stack" style={{ gap: 4 }}>
                  <span className="help">Email</span>
                  <div>{user.email || "—"}</div>
                </div>
              )}
            </>
          )}

          {isAthleteRecord && (
            <>
              {!canEditRoster && (
                <>
                  <div><strong>Graduation Year:</strong> {user.graduation_year || "—"}</div>
                  <div><strong>Birthdate:</strong> {user.birthdate || "—"}</div>
                  <div><strong>High School:</strong> {user.high_school || "—"}</div>
                  <div><strong>2K PR:</strong> {user.two_k_pr || "—"}</div>
                </>
              )}
              <div><strong>Age Group:</strong> {user.age_group || "—"}</div>
              <div><strong>Parent 1 Name:</strong> {user.parent1_name || "—"}</div>
              <div><strong>Parent 1 Phone:</strong> {user.parent1_phone || "—"}</div>
              <div><strong>Parent 2 Name:</strong> {user.parent2_name || "—"}</div>
              <div><strong>Parent 2 Phone:</strong> {user.parent2_phone || "—"}</div>
            </>
          )}

          {/* User Management Actions */}
          {canManageUsers && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 14 }}>User Management</strong>
              </div>
             
              <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  className="btn small"
                  onClick={handleSendInvite}
                  disabled={sendingInvite || !user.email}
                  title={!user.email ? "User has no email address" : "Send invite email to user"}
                >
                  {sendingInvite ? "Sending..." : "Send Invite"}
                </button>
                <button
                  type="button"
                  className={`btn small ${(user.is_active === false || user.is_active === 0) ? "" : "danger"}`}
                  onClick={handleToggleStatus}
                  disabled={togglingStatus}
                >
                  {togglingStatus ? "Processing..." : (user.is_active === false || user.is_active === 0) ? "Activate User" : "Deactivate User"}
                </button>
              </div>
              {(user.is_active === false || user.is_active === 0) && (
                <p className="help" style={{ color: "#dc2626", marginTop: 8, fontSize: 12 }}>
                  This account is currently deactivated and cannot log in.
                </p>
              )}
            </div>
          )}

          {canManageUsers && isCoachRecord && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
              <div className="row" style={{ justifyContent: "space-between", alignItems: "center", gap: 8 }}>
                <strong style={{ fontSize: 14 }}>Permissions</strong>
                <button
                  type="button"
                  style={linkButtonStyle}
                  onClick={() => setShowPermissions((prev) => !prev)}
                >
                  {showPermissions ? "Hide permissions" : "View permissions"}
                </button>
              </div>
              {showPermissions && (
                <div style={{ marginTop: 10 }}>
                  {loadingPermissions ? (
                    <p className="help">Loading permissions...</p>
                  ) : permissions.length === 0 ? (
                    <p className="help">No permissions available.</p>
                  ) : (
                    <div className="stack" style={{ gap: 8 }}>
                      {permissions.map((perm) => (
                        <label key={perm.key} className="row" style={{ gap: 8, alignItems: "center", fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={!!perm.allowed}
                            disabled={!!updatingPermission[perm.key]}
                            onChange={(e) => handleTogglePermission(perm.key, e.target.checked)}
                          />
                          <span>{perm.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Parent-Athlete Linking for Athlete records */}
          {canManageUsers && isAthleteRecord && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 14 }}>Linked Parents</strong>
              </div>
              <p className="help" style={{ fontSize: 12, marginBottom: 8 }}>
                Link parent accounts to this athlete to allow them to RSVP on behalf of their child.
              </p>

              {loadingRelationships ? (
                <p className="help">Loading relationships...</p>
              ) : (
                <>
                  {parentRelationships.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      {parentRelationships.map((rel) => (
                        <div
                          key={rel.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 0",
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          <button
                            type="button"
                            style={linkButtonStyle}
                            onClick={() => openLinkedUser(rel.parent_user_id)}
                            title="Open parent details"
                          >
                            {rel.parent_name}
                          </button>
                          <button
                            type="button"
                            className="btn small danger"
                            onClick={async () => {
                              if (!window.confirm(`Unlink ${user.name} from ${rel.parent_name}?`)) return;
                              try {
                                await apiFetch(`/parent/link/${rel.id}`, { method: "DELETE" });
                                setParentRelationships((prev) => prev.filter((r) => r.id !== rel.id));
                              } catch (e) {
                                setModalError(e?.message || "Failed to unlink parent");
                              }
                            }}
                          >
                            Unlink
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
                    <select
                      className="input"
                      value={selectedParentId}
                      onChange={(e) => setSelectedParentId(e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select a parent to link...</option>
                      {roster
                        .filter((u) => canonicalRole(u) === "parent")
                        .map((parent) => (
                          <option key={parent.user_id || parent.id} value={parent.user_id || parent.id}>
                            {parent.name}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="btn small"
                      disabled={!selectedParentId || linkingParent}
                      onClick={async () => {
                        if (!selectedParentId) return;
                        setLinkingParent(true);
                        setModalError("");
                        try {
                          await apiFetch("/parent/link", {
                            method: "POST",
                            body: JSON.stringify({
                              parentUserId: Number(selectedParentId),
                              athleteUserId: user.user_id || user.id,
                            }),
                          });
                          const relationships = await apiFetch("/parent/relationships");
                          const userId = user.user_id || user.id;
                          const athleteRels = relationships.filter((r) => r.athlete_user_id === userId);
                          setParentRelationships(athleteRels);
                          setSelectedParentId("");
                        } catch (e) {
                          setModalError(e?.message || "Failed to link parent");
                        } finally {
                          setLinkingParent(false);
                        }
                      }}
                    >
                      {linkingParent ? "Linking..." : "Link Parent"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Parent-Athlete Linking for Parent records */}
          {canManageUsers && isParentRecord && (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid #e5e7eb" }}>
              <div style={{ marginBottom: 8 }}>
                <strong style={{ fontSize: 14 }}>Linked Athletes</strong>
              </div>
              <p className="help" style={{ fontSize: 12, marginBottom: 8 }}>
                Link this parent to athletes to allow RSVP on their behalf.
              </p>

              {loadingRelationships ? (
                <p className="help">Loading relationships...</p>
              ) : (
                <>
                  {parentRelationships.length > 0 && (
                    <div style={{ marginBottom: 12 }}>
                      {parentRelationships.map((rel) => (
                        <div
                          key={rel.id}
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            padding: "6px 0",
                            borderBottom: "1px solid #e5e7eb",
                          }}
                        >
                          <button
                            type="button"
                            style={linkButtonStyle}
                            onClick={() => openLinkedUser(rel.athlete_user_id)}
                            title="Open athlete details"
                          >
                            {rel.athlete_name}
                          </button>
                          <button
                            type="button"
                            className="btn small danger"
                            onClick={async () => {
                              if (!window.confirm(`Unlink ${user.name} from ${rel.athlete_name}?`)) return;
                              try {
                                await apiFetch(`/parent/link/${rel.id}`, { method: "DELETE" });
                                setParentRelationships((prev) => prev.filter((r) => r.id !== rel.id));
                              } catch (e) {
                                setModalError(e?.message || "Failed to unlink athlete");
                              }
                            }}
                          >
                            Unlink
                          </button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="row" style={{ gap: 8, alignItems: "flex-end" }}>
                    <select
                      className="input"
                      value={selectedParentId}
                      onChange={(e) => setSelectedParentId(e.target.value)}
                      style={{ flex: 1 }}
                    >
                      <option value="">Select an athlete to link...</option>
                      {roster
                        .filter((u) => canonicalRole(u) === "athlete")
                        .map((athlete) => (
                          <option key={athlete.user_id || athlete.id} value={athlete.user_id || athlete.id}>
                            {athlete.name} {athlete.team ? `(${athlete.team})` : ""}
                          </option>
                        ))}
                    </select>
                    <button
                      type="button"
                      className="btn small"
                      disabled={!selectedParentId || linkingParent}
                      onClick={async () => {
                        if (!selectedParentId) return;
                        setLinkingParent(true);
                        setModalError("");
                        try {
                          await apiFetch("/parent/link", {
                            method: "POST",
                            body: JSON.stringify({
                              parentUserId: user.user_id || user.id,
                              athleteUserId: Number(selectedParentId),
                            }),
                          });
                          const relationships = await apiFetch("/parent/relationships");
                          const userId = user.user_id || user.id;
                          const parentRels = relationships.filter((r) => r.parent_user_id === userId);
                          setParentRelationships(parentRels);
                          setSelectedParentId("");
                        } catch (e) {
                          setModalError(e?.message || "Failed to link athlete");
                        } finally {
                          setLinkingParent(false);
                        }
                      }}
                    >
                      {linkingParent ? "Linking..." : "Link Athlete"}
                    </button>
                  </div>
                </>
              )}
            </div>
          )}

          {modalError && (
            <div className="help" style={{ color: "red", marginTop: 12 }}>
              {modalError}
            </div>
          )}

          {(canEditRoster || canManageUsers) && !isCoachRecord && (
            <div className="row" style={{ justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
              <button
                type="button"
                className="btn ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
          {(canManageUsers || canEditRoster) && isCoachRecord && (
            <div className="row" style={{ justifyContent: "flex-end", marginTop: 16, gap: 8 }}>
              <button
                type="button"
                className="btn ghost"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
